import { useEffect, useState, type FormEvent } from 'react';
import { Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, LinearProgress, Radio, Stack, TextField, Typography } from '@mui/material';
import type { CustomUser } from 'api-client';

interface Props {
  users: CustomUser[];
  currentUserId: number;
  csrfToken: string;
  loading: boolean;
  error: string | null;
  search: string;
  page: number;
  hasNext: boolean;
  onSearch: (value: string) => void;
  onPage: (value: number) => void;
  onRetry: () => void;
  pending?: boolean;
  onAcquire?: (user: CustomUser, event: FormEvent<HTMLFormElement>) => void;
}

export function ImpersonationPanel({ users, currentUserId, csrfToken, loading, error, search, page,
  hasNext, onSearch, onPage, onRetry, pending = false, onAcquire }: Props) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [confirming, setConfirming] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const busy = pending || submitting;
  const selected = users.find((user) => user.id === selectedId);
  const allowed = Boolean(selected?.isActive && selected.id !== currentUserId && csrfToken && !loading && !error);
  useEffect(() => {
    if (!busy) { setConfirming(false); setSelectedId(null); }
  }, [search, page, busy]);

  return <Stack spacing={3} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
    <Typography component="h1" variant="h4">Impersonate a user</Typography>
    <Alert severity="warning">Use this support tool responsibly. You will temporarily use the selected account’s permissions.
      A visible warning provides an action to end impersonation.</Alert>
    <Card><CardContent><Stack spacing={2}>
      <TextField label="Search users" value={search} disabled={busy}
        onChange={(event) => onSearch(event.target.value)} helperText="Search by email, username or name." fullWidth />
      {loading && <Box role="status"><Typography>Loading users…</Typography><LinearProgress /></Box>}
      {error && <Alert severity="error" action={<Button color="inherit" onClick={onRetry}>Retry</Button>}>{error}</Alert>}
      {!loading && !error && <>
        <Typography component="h2" variant="h6">Select a user</Typography>
        <Box role="radiogroup" aria-label="User to impersonate" sx={{ minWidth: 0 }}>
          {users.map((user) => {
            const reason = !user.isActive ? 'Inactive account: impersonation unavailable'
              : user.id === currentUserId ? 'Your account: impersonation unavailable' : null;
            return <Box key={user.id} sx={{ borderBottom: 1, borderColor: 'divider', py: 1 }}>
              <FormControlLabel sx={{ width: '100%', m: 0, alignItems: 'flex-start', minWidth: 0,
                '& .MuiFormControlLabel-label': { minWidth: 0, overflowWrap: 'anywhere' } }}
                control={<Radio name="support-user" value={user.id} checked={selectedId === user.id}
                  disabled={Boolean(reason) || busy} onChange={() => setSelectedId(user.id)} />}
                label={<Box sx={{ py: 1 }}>
                  <Typography fontWeight="medium">{user.getDisplayName || user.email || user.username}</Typography>
                  <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                  {reason && <Typography variant="body2" color="text.secondary">{reason}</Typography>}
                </Box>} />
            </Box>;
          })}
          {users.length === 0 && <Typography>No users match this search.</Typography>}
        </Box>
      </>}
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} alignItems="center">
        <Button variant="outlined" disabled={page <= 1 || loading || busy} onClick={() => onPage(page - 1)}>Previous</Button>
        <Typography>Page {page}</Typography>
        <Button variant="outlined" disabled={!hasNext || loading || busy || Boolean(error)} onClick={() => onPage(page + 1)}>Next</Button>
      </Stack>
      {!csrfToken && <Alert severity="error">Reload the page to restore the secure support action.</Alert>}
      <Button variant="contained" disabled={!allowed || busy} onClick={() => setConfirming(true)}>
        Impersonate selected user
      </Button>
    </Stack></CardContent></Card>
    <Dialog open={confirming} onClose={() => { if (!busy) setConfirming(false); }} fullWidth maxWidth="sm"
      aria-labelledby="impersonation-confirm-title">
      <DialogTitle id="impersonation-confirm-title">Confirm impersonation</DialogTitle>
      <Box component="form" action="/hijack/acquire/" method="post" onSubmit={(event: FormEvent<HTMLFormElement>) => {
        if (!selected || !allowed || busy) { event.preventDefault(); return; }
        onAcquire?.(selected, event);
        if (!event.defaultPrevented) setSubmitting(true);
      }}>
        <input type="hidden" name="csrfmiddlewaretoken" value={csrfToken} />
        <input type="hidden" name="user_pk" value={selected?.id || ''} />
        <input type="hidden" name="next" value="/" />
        <DialogContent sx={{ overflowWrap: 'anywhere' }}>
          <Typography gutterBottom>Continue as {selected?.email || selected?.username}?</Typography>
          <Alert severity="warning">Actions will use this account’s identity and permissions. End impersonation to return to your account.</Alert>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button disabled={busy} onClick={() => setConfirming(false)}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!allowed || busy}>
            {busy ? 'Starting impersonation…' : 'Confirm impersonation'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  </Stack>;
}
